/**
 * Монетний контур: 1 бал = 1 грн.
 *
 * Принципи, закладені в модель:
 *
 *  1. Гроші — це ЖУРНАЛ, а не число. Кожне нарахування і кожна виплата —
 *     окремий незмінний запис. Баланс завжди рахується як сума журналу,
 *     тому «бали пропали» неможливе за побудовою, а батько має повний аудит.
 *
 *  2. «Легко» і «складно» — ВІДНОСНО РІВНЯ САМОЇ ДИТИНИ, не абсолютно.
 *     Складне питання для Богдана і для Марка — різні питання. Інакше старший
 *     фармив би дитячі приклади, а молодший упирався б у стелю.
 *
 *  3. Система сама піднімає планку. Якщо дитина щодня бере лише «Тренування»,
 *     її Elo росте, і «легкі» питання стають об'єктивно складнішими.
 *     Фармити одне й те саме неможливо — рівень тягнеться за дитиною.
 *
 *  4. Гроші спрямовані на РОЗВИТОК, а не на обсяг: множник за слабкий предмет
 *     робить математику (якщо вона провисає) найвигіднішою темою дня.
 */

// ─────────────────────────────────────────────────────────────────────────────
// Режими складності, які обирає дитина перед сесією
// ─────────────────────────────────────────────────────────────────────────────

export const DIFFICULTY_MODES = ['training', 'showdown', 'challenge'] as const;
export type DifficultyMode = (typeof DIFFICULTY_MODES)[number];

export interface DifficultyModeSpec {
  id: DifficultyMode;
  title: string;
  subtitle: string;
  /** Частка складних питань у сесії. */
  hardRatio: number;
  /** Цільова ймовірність успіху для легких питань цього режиму. */
  easyTargetSuccess: number;
  /** Цільова ймовірність успіху для складних питань. */
  hardTargetSuccess: number;
  /** Бонус до всієї сесії за її повне проходження, у балах. */
  completionBonus: number;
  brandAsset: string;
}

/**
 * Три режими. Дитина бачить чесний обмін: більший ризик — більша ставка.
 *
 * `training`  — усе по 1 балу, висока ймовірність успіху. Безпечний заробіток.
 * `showdown`  — суміш; складні йдуть у випадкових позиціях, тож не можна
 *               «здатися» на складному й чекати легкого.
 * `challenge` — усе по 2 бали, але й провалів буде помітно більше.
 */
export const DIFFICULTY_MODE_SPECS: Record<DifficultyMode, DifficultyModeSpec> = {
  training: {
    id: 'training',
    title: 'Тренування',
    subtitle: 'Простіші питання · 1 бал за кожне',
    hardRatio: 0,
    easyTargetSuccess: 0.85,
    hardTargetSuccess: 0.85,
    completionBonus: 0,
    brandAsset: '/brand/modes/mode-training.png',
  },
  showdown: {
    id: 'showdown',
    title: 'Сутичка',
    subtitle: 'Прості та складні вперемішку · 1 і 2 бали',
    hardRatio: 0.4,
    easyTargetSuccess: 0.85,
    hardTargetSuccess: 0.62,
    completionBonus: 2,
    brandAsset: '/brand/modes/mode-showdown.png',
  },
  challenge: {
    id: 'challenge',
    title: 'Виклик',
    subtitle: 'Тільки складні питання · 2 бали за кожне',
    hardRatio: 1,
    easyTargetSuccess: 0.62,
    hardTargetSuccess: 0.62,
    completionBonus: 4,
    brandAsset: '/brand/modes/mode-challenge.png',
  },
};

/** Смуга складності конкретного питання всередині сесії. */
export type DifficultyBand = 'easy' | 'hard';

/** Базова ставка в балах (= гривнях) за правильну відповідь. */
export const COIN_VALUE: Record<DifficultyBand, number> = {
  easy: 1,
  hard: 2,
};

// ─────────────────────────────────────────────────────────────────────────────
// Правила нарахування
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Мінімальний час на відповідь, секунд. Швидше — це не відповідь, а тикання:
 * прочитати умову й обрати варіант за пів секунди неможливо.
 *
 * Правило спільне для ОБОХ шкал — і балів, і XP. Живе окремою сталою саме
 * тому: поки воно лежало всередині `COIN_RULES`, назва підказувала, що це
 * справа грошей, і `computeXp` про нього просто не знав. Наслідок був
 * зворотний до задуманого — дитина, яка прокликує варіанти навмання, грошей
 * не заробляла, зате качала рівень ШВИДШЕ за ту, що думає: миттєвий клік ще й
 * потрапляв у бонус за швидкість.
 */
export const MIN_RESPONSE_SEC = 1.5;

export const COIN_RULES = {
  /**
   * Множник за питання зі слабкого предмета дитини.
   * Саме він робить «підтягни математику» вигідним, а не примусовим.
   */
  WEAK_SUBJECT_MULTIPLIER: 1.5,
  /** Наскільки нижче середнього має бути предмет, щоб рахуватись слабким (0..1). */
  WEAK_SUBJECT_THRESHOLD: 0.12,

  /**
   * Денний ліміт заробітку, балів. **0 = без ліміту** — саме так і налаштовано
   * за замовчуванням: скільки дитина заробила, стільки й заробила.
   * Єдине обмеження в системі — вікно доступу за розкладом.
   *
   * Поле лишається в моделі, бо ліміт може знадобитися пізніше; щоб його
   * увімкнути, досить поставити додатне число в кабінеті батьків.
   */
  DEFAULT_DAILY_CAP: 0,

  /**
   * Бал нараховується лише за ПЕРШУ правильну відповідь на це питання
   * у межах вікна. Повторне влучання в те саме питання дає XP, але не гроші.
   */
  REPEAT_COOLDOWN_DAYS: 30,

  /**
   * Якщо дитина відкрила ВСІ підказки — питання зараховується як навчальне:
   * XP і прогрес нараховуються, бал — ні. Одна-дві підказки бал не знімають.
   */
  ZERO_COIN_IF_ALL_HINTS: true,

  /**
   * Бонус за виправлення раніше зробленої помилки (повернення до питання,
   * яке колись провалили, і правильна відповідь). Це найцінніша подія
   * у навчанні, тому вона окремо оплачується.
   */
  REDEMPTION_BONUS: 1,

  /** Те саме, що `MIN_RESPONSE_SEC` — лишається тут заради наявних викликів. */
  MIN_RESPONSE_SEC,

  /*
   * Бонусу за денну ціль тут НЕМАЄ і не буде. Стала `DAILY_GOAL_BONUS` колись
   * лежала поруч і обіцяла три бали, яких ніхто ніколи не нараховував: жоден
   * рядок її не читав. Денна ціль у цьому проєкті — про кількість пройдених
   * ПИТАНЬ, а не про гроші; за неї платить смуга поступу на арені, не журнал.
   */
} as const;

/**
 * Вивід зароблених балів готівкою.
 *
 * Поріг навмисно високий: щоденний вивід дрібними сумами перетворює навчання
 * на касу, а накопичення до відчутної суми вчить чекати й планувати —
 * це, власне, і є фінансова грамотність, яку ми окремо тренуємо в задачах.
 */
export const PAYOUT_RULES = {
  /** Мінімальна сума виводу, балів (= гривень). */
  MIN_WITHDRAWAL: 1000,
} as const;

export interface CoinAwardInput {
  band: DifficultyBand;
  isCorrect: boolean;
  responseMs: number;
  hintsUsed: number;
  hintsAvailable: number;
  /** Чи це питання вже приносило бал у межах COOLDOWN. */
  alreadyPaidRecently: boolean;
  /** Чи належить питання до слабкого предмета дитини. */
  isWeakSubject: boolean;
  /** Чи це успішне повернення до колись проваленого питання. */
  isRedemption: boolean;
  /** Скільки балів дитина вже заробила сьогодні. */
  earnedToday: number;
  dailyCap: number;
  /**
   * Поріг «це вже тикання», секунд. За замовчуванням — груба стала.
   *
   * Сервер підставляє сюди більше з двох: сталої й часу, за який умову ФІЗИЧНО
   * можна прочитати (`minPlausibleResponseSec`). Без цього лишалася дірка: три
   * секунди на задачу в сорок слів проходили поріг у 1,5 с, а при чотирьох
   * варіантах кожне четверте влучання оплачувалося. Рейтинг від вгадування
   * уже не росте, тож питання легшими не ставали, — але сам заробіток
   * навмання лишався можливим.
   */
  minResponseSec?: number;
}

export interface CoinAwardResult {
  /** Скільки балів (= гривень) нараховано. Ціле число. */
  coins: number;
  /** Причини — показуються дитині як «за що» і зберігаються в журналі. */
  reasons: string[];
  /** Чи впёрлися в денний ліміт. */
  cappedByDailyLimit: boolean;
}

/**
 * Чиста функція нарахування. Живе у shared, бо однакова логіка потрібна
 * і на сервері (авторитетний розрахунок), і на клієнті (миттєвий показ).
 * Авторитетним є ЗАВЖДИ результат сервера.
 */
export function computeCoinAward(input: CoinAwardInput): CoinAwardResult {
  const reasons: string[] = [];

  if (!input.isCorrect) {
    return { coins: 0, reasons: [], cappedByDailyLimit: false };
  }
  if (input.responseMs < (input.minResponseSec ?? MIN_RESPONSE_SEC) * 1000) {
    return { coins: 0, reasons: ['too_fast'], cappedByDailyLimit: false };
  }
  if (input.alreadyPaidRecently) {
    return { coins: 0, reasons: ['already_paid'], cappedByDailyLimit: false };
  }
  if (
    COIN_RULES.ZERO_COIN_IF_ALL_HINTS &&
    input.hintsAvailable > 0 &&
    input.hintsUsed >= input.hintsAvailable
  ) {
    return { coins: 0, reasons: ['all_hints_used'], cappedByDailyLimit: false };
  }

  let coins = COIN_VALUE[input.band];
  reasons.push(input.band === 'hard' ? 'hard_question' : 'easy_question');

  if (input.isWeakSubject) {
    coins = Math.ceil(coins * COIN_RULES.WEAK_SUBJECT_MULTIPLIER);
    reasons.push('weak_subject_boost');
  }
  if (input.isRedemption) {
    coins += COIN_RULES.REDEMPTION_BONUS;
    reasons.push('redemption');
  }

  // dailyCap <= 0 означає «ліміту немає» — тоді ця гілка взагалі не працює.
  if (input.dailyCap > 0) {
    const remaining = Math.max(0, input.dailyCap - input.earnedToday);
    if (coins > remaining) {
      coins = remaining;
      reasons.push('daily_cap');
      return { coins, reasons, cappedByDailyLimit: true };
    }
  }

  return { coins, reasons, cappedByDailyLimit: false };
}

// ─────────────────────────────────────────────────────────────────────────────
// Журнал (ledger)
// ─────────────────────────────────────────────────────────────────────────────

export const LEDGER_KINDS = [
  'earn', // за правильну відповідь
  'session_bonus', // бонус за завершення сесії у складному режимі
  'goal_bonus', // денна/тижнева ціль
  'achievement', // досягнення
  'payout', // батько виплатив готівкою — від'ємна сума
  'adjustment', // ручна правка батьком (в обидва боки), з обов'язковою нотаткою
] as const;
export type LedgerKind = (typeof LEDGER_KINDS)[number];

/**
 * Один запис журналу. Тільки додавання, ніколи не оновлення й не видалення.
 * Баланс = SUM(amount). Виплачено = -SUM(amount WHERE kind='payout').
 */
export interface LedgerEntry {
  id: string;
  learnerId: string;
  kind: LedgerKind;
  /** Цілі бали. Додатні — нарахування, від'ємні — виплата. */
  amount: number;
  /** Посилання на спробу, якщо запис породжений відповіддю. */
  attemptId: string | null;
  reasons: string[];
  note: string | null;
  /** Хто створив запис: система чи конкретний батьківський акаунт. */
  createdBy: string;
  createdAt: string;
}

export interface WalletSummary {
  learnerId: string;
  /** Скільки зароблено за весь час. */
  totalEarned: number;
  /** Скільки вже виплачено готівкою. */
  totalPaidOut: number;
  /** До виплати = totalEarned - totalPaidOut. */
  balance: number;
  earnedToday: number;
  earnedThisWeek: number;
  /** Стеля заробітку за добу, балів. 0 = без ліміту. Це НЕ денна ціль. */
  dailyCap: number;
  /** Денна ціль у питаннях із кабінету батьків. 0 = ціль не задана. */
  dailyGoalItems: number;
  /** Скільки питань пройдено сьогодні — цим міряється поступ до цілі. */
  answeredToday: number;
}
